import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { isColumnShown } from '../_custom/DataTable/DataTable.constants';
import { CellRendererMap, DataTableColumn, DataTableColumnType } from '../_custom/DataTable/DataTable.types';
import { DateCellRenderer, TextCellRenderer } from '../_custom/DataTable/components/CellRenderers';
import { useAppSelector } from '../../app/hooks';
import { closeCommentsThread } from '../../store/commentsThread/commentsThread_actions';
import {
    ReadOnlyCompanyNameRenderer,
    ReadOnlyLastStatusUpdateRenderer,
    makeReadOnlyModalActionRenderer,
    withReadOnlyFieldBox,
} from './readOnlyRenderers';
import { filterColumnsByLabel } from './filterPropertyColumns';
import { ViewAllPropertiesDrawerProps } from './types';

const DEFAULT_READ_ONLY_RENDERERS: CellRendererMap = {
    [DataTableColumnType.TEXT]: withReadOnlyFieldBox(TextCellRenderer),
    [DataTableColumnType.DATE_FORMAT]: withReadOnlyFieldBox(DateCellRenderer),
};

type Params = Pick<ViewAllPropertiesDrawerProps, 'columns' | 'customCellRenderers' | 'modalActionConfig' | 'onClose'> & {
    rowKey: number;
};

export function useViewAllPropertiesLogic({ columns, customCellRenderers, modalActionConfig, onClose, rowKey }: Params) {
    const dispatch = useDispatch();
    // The query is stored with the row key it was typed for, so a different row reads '' in the same render (no stale frame).
    const [search, setSearch] = useState({ rowKey, query: '' });
    const searchQuery = search.rowKey === rowKey ? search.query : '';
    const setSearchQuery = useCallback((query: string) => setSearch({ rowKey, query }), [rowKey]);
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);

    // The CommentsThread is an in-flow panel, so the backdrop would cover it without closing it.
    useEffect(() => {
        if (commentsThreadState.showCommentsThread) {
            dispatch(closeCommentsThread());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Clicking a COMMENT cell opens the thread panel, so close the drawer on that false->true edge.
    const wasThreadOpenRef = useRef(commentsThreadState.showCommentsThread);
    useEffect(() => {
        const wasOpen = wasThreadOpenRef.current;
        wasThreadOpenRef.current = commentsThreadState.showCommentsThread;
        if (!wasOpen && commentsThreadState.showCommentsThread) {
            onClose();
        }
    }, [commentsThreadState.showCommentsThread, onClose]);

    const readOnlyOverrides: CellRendererMap = useMemo(
        () => ({
            [DataTableColumnType.LAST_STATUS_UPDATE]: withReadOnlyFieldBox(ReadOnlyLastStatusUpdateRenderer),
            [DataTableColumnType.COMPANY_NAME_CTA]: withReadOnlyFieldBox(ReadOnlyCompanyNameRenderer),
            [DataTableColumnType.MODAL_ACTION]: withReadOnlyFieldBox(makeReadOnlyModalActionRenderer(modalActionConfig)),
        }),
        [modalActionConfig],
    );

    const mergedCellRenderers = useMemo(
        () => ({ ...DEFAULT_READ_ONLY_RENDERERS, ...customCellRenderers, ...readOnlyOverrides }),
        [customCellRenderers, readOnlyOverrides],
    );

    const visibleColumns: DataTableColumn[] = useMemo(
        () => filterColumnsByLabel(columns.filter(isColumnShown), searchQuery),
        [columns, searchQuery],
    );
    const hasNoMatches = visibleColumns.length === 0 && !!searchQuery.trim();

    return { searchQuery, setSearchQuery, visibleColumns, hasNoMatches, mergedCellRenderers };
}
